/**
 * Sticky column pinning composed with row-only virtualization. TanStack ships the
 * pinning STATE and offset arithmetic (`column.getStart` / `getAfter`) but ZERO
 * pinning CSS; this file adds the sticky/z-index/opaque-bg/RTL CSS plus the offset
 * map and the group-gutter widths that place the continuous shadow rail
 * (`PinnedColumnShadows`). Offsets are read ONCE per render into a `columnId -> offset` map
 * so the O(columns) sum never runs on the per-cell windowed hot path; header, body,
 * and skeleton index the SAME map, so a pinned cell lands at an identical edge in
 * all three (a divergence is the "header pin drifts from body pin on scroll" bug).
 */
import type { CSSProperties } from 'react'
import type {
  Column,
  ColumnDef,
  ColumnOrderState,
  ColumnPinningPosition,
  ColumnPinningState,
  ColumnSizingState,
  RowData,
  Table,
  VisibilityState,
} from '@tanstack/react-table'

import { type DataTableSurface, SURFACE_BG } from './data-table.styles'

/**
 * Z-index layers reserved inside the table's `isolate` stacking context (opened by
 * `dataTableRootVariants`). The numbers ARE the bottom-to-top paint order, split
 * into a BODY band and the two CHROME bands that sandwich it:
 *
 *   body(0) < pinnedBody(2) < rowOverlay(3) < focus(4)  │  summary(5) < header(6) < pinnedHeader(7)
 *
 * Both chrome bands out-rank the whole BODY band, so the sticky header and the sticky
 * summary row each occlude any body cell (focused or pinned) that scrolls under them.
 * Within the body band a focused cell (focus=4) must out-rank an overlapping pinned
 * column (pinnedBody=2), or its ring is clipped by the opaque pinned background (C-397).
 * The summary sits BELOW the header so the header wins the (degenerate) overlap where a
 * bounded height is shorter than the two bands together.
 */
export const PINNED_Z_INDEX = Object.freeze({
  body: 0,
  /** Pinned body cells: above unpinned body so it scrolls under them. */
  pinnedBody: 2,
  /** Row hover/selection `::before` tint: above the opaque pinned surface (so the tint
   * reaches pinned columns) yet below focus + header. A renumber must keep
   * `pinnedBody < rowOverlay < focus < header`. */
  rowOverlay: 3,
  /** Focused body cell: above pinned body surfaces so its ring isn't clipped by an
   * overlapping pinned column (C-397); below the chrome bands. */
  focus: 4,
  /** Sticky summary row (either edge): above the entire body band, below the header band.
   * Mirrored by `dataTableSummaryBandVariants`. */
  summary: 5,
  /** Sticky header row: above the entire body band. Mirrored by `dataTableHeaderVariants`. */
  header: 6,
  /** Pinned header cells: above unpinned header cells within the header row. */
  pinnedHeader: 7,
})

/**
 * RTL mirror for a pinned column's physical edge: in RTL the inline-start edge is the
 * screen's right, so a logically-left pin sticks to `right` and vice versa.
 */
const MIRRORED_EDGE = Object.freeze({ left: 'right', right: 'left' } as const)

/** One pinned column's resolved horizontal offset and its position in the group. */
export type PinnedOffset = {
  side: Exclude<ColumnPinningPosition, false>
  /** Cumulative px offset from the pinned edge. */
  offset: number
  /**
   * The center-most (inner) column of the group — its `data-pinned-last` marks the group's
   * boundary in the DOM. The drop shadow is NOT painted here anymore (it restarted at every
   * floating row); it is a single continuous rail from `PinnedColumnShadows`. Kept as the
   * edge marker consumers/tests key on and for debugging which column closes the group.
   */
  isLastPinned: boolean
}

/** `columnId -> PinnedOffset` for every pinned column; unpinned columns are absent. */
export type PinnedOffsetMap = ReadonlyMap<string, PinnedOffset>

/** Pin-state data attributes a pinned cell carries; the sticky CSS below targets them. */
export type PinnedDataAttributes = {
  'data-pinned'?: 'left' | 'right'
  'data-pinned-last'?: 'true'
}

/**
 * Inline + attribute payload for a pinned cell, or `undefined` when unpinned — an
 * unpinned cell must NOT receive `position: sticky`, or it stops scrolling. Returns
 * only the per-column dynamic bits (edge + offset); the opaque bg / z-index live in the
 * CSS classes, so the effect is identical across header/body/skeleton. The drop shadow is
 * no longer per-cell — it is one continuous rail (`PinnedColumnShadows`).
 */
export const getPinnedStyle = (
  entry: PinnedOffset | undefined,
  rtl: boolean,
): { style: CSSProperties; attributes: PinnedDataAttributes } | undefined => {
  if (entry === undefined) {
    return undefined
  }

  const physicalEdge: 'left' | 'right' = rtl ? MIRRORED_EDGE[entry.side] : entry.side

  const style: CSSProperties = {
    position: 'sticky',
    [physicalEdge]: entry.offset,
    zIndex: PINNED_Z_INDEX.pinnedBody,
  }

  const attributes: PinnedDataAttributes = {
    'data-pinned': entry.side,
    ...(entry.isLastPinned ? { 'data-pinned-last': 'true' as const } : {}),
  }

  return { style, attributes }
}

/**
 * The OPAQUE background a pinned cell paints so scrolled unpinned content can't bleed
 * through it (a transparent pinned cell is the #1 pinning bug), in the surface of the
 * PLANE the cell sits in — the card's for a body cell, its own band's for a header or
 * summary cell. `transition-colors` animates it in step with the row's hover/selected
 * change. The one source for every pinned cell, model-backed (`getPinnedCellProps`) or
 * model-external (the leading selection column).
 */
export const pinnedCellSurfaceClass = (surface: DataTableSurface): string =>
  `${SURFACE_BG[surface]} transition-colors`

/** Width (px) of the continuous pinned-column shadow rail — the gradient's fade distance. */
export const PIN_SHADOW_WIDTH_PX = 16

/**
 * Continuous drop shadow for a pinned group, as ONE full-height gradient RAIL per side
 * (rendered by `PinnedColumnShadows`) instead of a per-cell `box-shadow`. The per-cell
 * shadow restarted at every floating row — the 1px inter-row gap + `rounded-sm` corners
 * chopped it into per-row segments; a single rail reads as one shadow cast down the whole
 * pinned column. Keyed by the side the group sits on: a LEFT group casts to the right
 * (dark at the group's inner edge → transparent), a RIGHT group casts to the left. Black
 * rgba (theme-independent, MUI pinned-shadow parity); the fade is linear over
 * `PIN_SHADOW_WIDTH_PX`. No border by design — MUI draws no divider line on pinned columns.
 */
export const pinnedShadowRailClass: Record<'left' | 'right', string> = {
  left: 'bg-[linear-gradient(to_right,rgba(0,0,0,0.3),rgba(0,0,0,0))]',
  right: 'bg-[linear-gradient(to_left,rgba(0,0,0,0.3),rgba(0,0,0,0))]',
}

/**
 * Engine wiring: `getPinnedColumnOffsets` + `getPinnedCellProps` are the only
 * engine-aware pinning glue — they read the pinned columns + their engine offsets off
 * the `table`, build the map once, and hand a spread payload to the engine-decoupled
 * cell parts via their `pinned` prop, keeping the presentational parts pure.
 */

/**
 * EVERY input the pinned geometry derives from, as referenced VALUES — the argument both
 * geometry readers take purely so a `useMemo` at the call site can key on them.
 *
 * The readers walk the referentially-stable `table`, so nothing about their arguments tells
 * React (or `exhaustive-deps`) when the answer changes. Listing the inputs here keeps the
 * dep set in ONE place next to the code that consumes it, instead of duplicated at each
 * memo site where it can silently fall behind: the offsets map lost `columnDefs` exactly
 * that way, and the gutter widths went unmemoized entirely while reading the same geometry.
 *
 * `columnDefs` is not optional trivia — `getSize()` resolves
 * `columnSizing[id] ?? columnDef.size ?? default`, so a consumer that changes a column's
 * authored `size` without touching any state slice moves every pinned offset.
 */
export type PinnedGeometryMemoKey<TData extends RowData> = {
  columnDefs: ColumnDef<TData, never>[] | readonly unknown[]
  columnPinningState: ColumnPinningState
  columnSizingState: ColumnSizingState
  columnOrderState: ColumnOrderState
  columnVisibilityState: VisibilityState
}

/**
 * Build the left/right pinned-offset map, reading each offset from the engine's own
 * memoized `column.getStart('left')` / `getAfter('right')`; each cell then reads its
 * offset in O(1), so the O(columns) sum never runs on the windowed hot path.
 *
 * `leftInset` shifts every left-pinned column right by a fixed px: the leading
 * selection column is hand-rendered OUTSIDE the TanStack model, so it is absent from
 * `getStart`'s sum yet occupies the left edge a left-pinned DATA column must clear.
 *
 * `_memoKey` is unused by the body on purpose — see `PinnedGeometryMemoKey`.
 */
export const getPinnedColumnOffsets = <TData extends RowData>(
  table: Table<TData>,
  leftInset = 0,
  _memoKey?: PinnedGeometryMemoKey<TData>,
): PinnedOffsetMap => {
  const map = new Map<string, PinnedOffset>()

  for (const column of table.getLeftVisibleLeafColumns()) {
    map.set(column.id, {
      side: 'left',
      offset: column.getStart('left') + leftInset,
      isLastPinned: column.getIsLastColumn('left'),
    })
  }

  for (const column of table.getRightVisibleLeafColumns()) {
    map.set(column.id, {
      side: 'right',
      offset: column.getAfter('right'),
      isLastPinned: column.getIsFirstColumn('right'),
    })
  }

  return map
}

/** Total sticky px width of each pinned group; `0` on a side with no pins. */
export type PinnedGutterWidths = { left: number; right: number }

const sumColumnWidths = <TData extends RowData>(columns: Column<TData, unknown>[]): number =>
  columns.reduce((total, column) => total + column.getSize(), 0)

/**
 * Sum the sticky width of the left + right pinned groups, so `PinnedColumnShadows` can
 * place each continuous shadow rail exactly at the group's INNER edge (`left: gutter.left`
 * / `right: gutter.right`). `leftInset` (the model-external leading selection column — the
 * same seed `getPinnedColumnOffsets` takes) is added to the LEFT gutter ONLY when a data
 * column is actually left-pinned: a lone unpinned selection column is not a sticky group
 * and casts no shadow. A side with no pinned columns returns `0`, and the preset renders no
 * rail there.
 */
export const getPinnedGutterWidths = <TData extends RowData>(
  table: Table<TData>,
  leftInset = 0,
  _memoKey?: PinnedGeometryMemoKey<TData>,
): PinnedGutterWidths => {
  const leftColumns = table.getLeftVisibleLeafColumns()
  const rightColumns = table.getRightVisibleLeafColumns()

  return {
    left: leftColumns.length === 0 ? 0 : leftInset + sumColumnWidths(leftColumns),
    right: sumColumnWidths(rightColumns),
  }
}

/** The `pinned` payload spread onto a `TableCell` / `TableHeaderCell`: sticky style + opaque-surface class + pin data-attrs. */
export type PinnedCellProps = {
  style: CSSProperties
  className: string
} & PinnedDataAttributes

/**
 * Build the `pinned` payload for one cell from the map; `undefined` for an unpinned
 * column, so it never gets `position: sticky`. Header/body/summary pass the same
 * `columnId` and get the same offset/attrs, differing only by `variant` (the header
 * z-lift) — which keeps the planes aligned. The drop shadow is NOT per-cell (it would
 * restart at every floating row); it is one continuous rail rendered separately by
 * `PinnedColumnShadows`.
 *
 * `surface` is the plane's own elevation (see `pinnedCellSurfaceClass`), not a
 * per-variant constant: a table lifted off the default elevation2 card would otherwise
 * show its pinned columns as a patch of the wrong elevation.
 */
export const getPinnedCellProps = (
  offsetMap: PinnedOffsetMap,
  columnId: string,
  {
    variant,
    surface,
    rtl = false,
  }: { variant: 'body' | 'header'; surface: DataTableSurface; rtl?: boolean },
): PinnedCellProps | undefined => {
  const pinned = getPinnedStyle(offsetMap.get(columnId), rtl)
  if (pinned === undefined) {
    return undefined
  }

  const { style, attributes } = pinned
  // A pinned HEADER cell lifts to `pinnedHeader` so it stays above unpinned header
  // cells within the header row's own stacking context; body keeps pinnedBody.
  const zIndex = variant === 'header' ? PINNED_Z_INDEX.pinnedHeader : style.zIndex

  return {
    style: { ...style, zIndex },
    className: pinnedCellSurfaceClass(surface),
    ...attributes,
  }
}
